import { get } from 'lodash';
import http from '../utils/http';
import { notifySuccess, notifyError } from './common';
import { ccUtils as utils } from '@/common/Utils';
import appFeatures from '@/appFeatures';
import { Promise as bbPromise } from 'bluebird';

const defaultState = {
    entityName: 'device',
    baseUrl: 'api/devices/',
    hawkeyeUrl: 'api/hawkeye/',
    device: {},
    spaceInfo: {},
    preferences: {},
    makeOptions: [],
    modelOptions: [],
    readHelpUrl: '/user-guide/guide/device-detail.html',
    editHelpUrl: '/user-guide/guide/device-edit.html'
};

export const mutations = {
    setDevice(state, value) {
        if (value) {
            state.device = value;
        } else {
            state.device = {};
        }
    },
    setPreferences(state, value) {
        if (value) {
            state.preferences = value;
        } else {
            state.preferences = {};
        }
    },
    setSpaceInfo(state, value) {
        if (value) {
            state.spaceInfo = value;
        } else {
            state.spaceInfo = {};
        }
    },
    setMakeOptions(state, value) {
        state.makeOptions = Array.isArray(value) ? value : [];
    },
    setModelOptions(state, value) {
        state.modelOptions = Array.isArray(value) ? value : [];
    }
};

export const actions = {
    fetchDevice({ state }, { deviceId, notifyConfig }) {
        console.log('calling fetchDevice', deviceId);
        const defaultConfig = {
            notifyRequestPending: true,
            ...notifyConfig
        };
        return http.get(`${state.baseUrl}${deviceId}`, defaultConfig);
    },
    fetchDeviceDetails({ commit, dispatch }, deviceId) {
        console.log('calling fetchDeviceDetails', deviceId);
        return dispatch('fetchDevice', { deviceId })
            .then(res => {
                commit('setDevice', res.data);
                return res;
            })
            .then(res => {
                if (res.data.serviceAccount) {
                    return dispatch(
                        'space/fetchSpaceByServiceAccount',
                        res.data.serviceAccount.id,
                        { root: true }
                    );
                }
            })
            .then(res => {
                if (res && res.content) {
                    commit('setSpaceInfo', res.content[0]);
                }
            })
            .then(() => {
                return dispatch('fetchPreferences', deviceId);
            });
    },
    fetchHealthStatus({ commit, state }, { hostMac, notifyRequestPending }) {
        console.log('calling fetchHealthStatus', hostMac);
        return http
            .get(`${state.hawkeyeUrl}health/?hostmac=${hostMac}`, {
                notifyRequestPending: notifyRequestPending || false,
                notifyError: false
            })
            .then(res => {
                commit('setDevice', {
                    ...state.device,
                    healthCheckStatus: res.data.healthStatus,
                    healthCheckStatusDate: new Date().toISOString()
                });
            });
    },
    fetchPreferences({ commit, state }, deviceId) {
        console.log('calling fetchPreferences', deviceId);
        return http
            .get(`${state.baseUrl}${deviceId}/preferences`, {
                notifyRequestPending: false,
                notifyError: false
            })
            .then(res => {
                commit('setPreferences', res.data);
            })
            .catch(() => {
                console.log('device preferences fetch error!!!');
            });
    },
    resetDevice({ commit }) {
        // will clear the current device info
        console.log('calling resetDevice');
        commit('setDevice', {});
        commit('setPreferences', {});
        commit('setSpaceInfo', {});
    },
    fetchDeviceOptions({ state, dispatch }) {
        if (!state.makeOptions.length) {
            dispatch('fetchMakeOptions');
        }
        if (!state.modelOptions.length) {
            dispatch('fetchModelOptions');
        }
    },
    fetchMakeOptions({ commit }) {
        const makes = get(
            appFeatures,
            'views.devices.displaySettings.options.makeOptions'
        );
        commit('setMakeOptions', makes);
    },
    fetchModelOptions({ commit }) {
        const models = get(
            appFeatures,
            'views.devices.displaySettings.options.modelOptions'
        );
        commit('setModelOptions', models);
    },
    fetchChannelOptions({ state }, { deviceId }) {
        console.log('calling fetchChannelOptions', deviceId);
        return http
            .get(`${state.baseUrl}${deviceId}/channelOptions`, {
                notifyRequestPending: true
            })
            .then(res => {
                if (res && res.data && res.data.channels) {
                    return res.data.channels;
                }
            });
    },
    createDeviceEntity({ dispatch }, { device, tags }) {
        console.log('calling createDeviceEntity');
        const successMessage = 'Device has been saved successfuly';
        const failMessage = 'Error occured while saving new device...';
        if (device) {
            dispatch('notifier/requestPending', null, { root: true });
            device.type = device.type = 'IpStb';
            return dispatch('createDevice', { device })
                .then(createdDevice => {
                    return dispatch('addTags', {
                        device: createdDevice,
                        tagIds: tags.map(item => item.id)
                    });
                })
                .then(updatedDevice => {
                    dispatch('fetchHealthStatus', {
                        hostMac: updatedDevice.hostMac,
                        notifyRequestPending: false
                    });
                })
                .then(() => {
                    notifySuccess(dispatch, successMessage);
                })
                .catch(error => {
                    notifyError(dispatch, failMessage, error);
                })
                .finally(() => {
                    dispatch('notifier/reset', null, { root: true });
                });
        }
    },
    updateDeviceEntity({ dispatch }, { oldDevice, newDevice }) {
        console.log('calling updateDeviceEntity', oldDevice.id);
        const successMessage = 'Device has been saved successfuly';
        const failMessage = 'Error occured while saving this device...';
        if (newDevice) {
            dispatch('notifier/requestPending', null, { root: true });
            return dispatch('updateDevice', {
                oldDevice,
                newDevice
            })
                .then(() => {
                    return dispatch('updateTags', {
                        device: oldDevice,
                        oldTags: oldDevice.tags,
                        newTags: newDevice.tags
                    });
                })
                .then(res => {
                    if (oldDevice.defaultChannel !== newDevice.defaultChannel) {
                        return dispatch('updateDefaultChannel', {
                            deviceId: newDevice.id,
                            defaultChannel: newDevice.defaultChannel
                        });
                    }
                    return res.data;
                })
                .then(() => {
                    notifySuccess(dispatch, successMessage);
                })
                .catch(error => {
                    notifyError(dispatch, failMessage, error);
                })
                .finally(() => {
                    dispatch('notifier/reset', null, { root: true });
                });
        }
    },
    createDevice({ state }, { device }) {
        // will create new device object
        console.log('calling createDevice');
        if (!device.type) {
            device.type = 'IpStb';
        }
        return http
            .post(state.baseUrl, device, {
                notifyRequestPending: false,
                notifySuccess: false,
                notifyError: false
            })
            .then(res => res.data);
    },
    updateDevice({ state }, { oldDevice, newDevice }) {
        // will update existing device
        console.log('calling updateDevice');
        if (oldDevice.serviceAccount && oldDevice.name !== newDevice.name) {
            return http
                .patch(
                    `${state.baseUrl}${newDevice.id}/logicalName`,
                    newDevice,
                    {
                        notifyRequestPending: false,
                        notifySuccess: false,
                        notifyError: false
                    }
                )
                .then(res => res.data);
        }
        return oldDevice;
    },
    deleteDevice({ dispatch, state }, { device }) {
        console.log('calling deleteDevice', device.id);
        return http
            .delete(`${state.baseUrl}${device.id}`, null, {
                notifyRequestPending: true,
                notifyError: true,
                notifySuccess: true,
                successMessage: 'Device, deleted successfully.',
                errorMessage: 'Device delete failed!'
            })
            .then(() => {
                setTimeout(() => {
                    dispatch(
                        'spaces/fetchAllSpaces',
                        {},
                        {
                            root: true
                        }
                    );
                }, 300);
            });
    },
    updateTags({ dispatch }, { device, oldTags, newTags }) {
        // will add/delete tags from a device
        console.log('calling updateTags', device.id);
        const tagsDiff = utils.diffArray(
            oldTags.map(item => item.id),
            newTags.map(item => item.id)
        );
        let actions = [];
        if (tagsDiff && Object.keys(tagsDiff).length) {
            if (tagsDiff.add && tagsDiff.add.length) {
                actions.push({
                    action: 'addTags',
                    payload: {
                        device,
                        tagIds: tagsDiff.add
                    }
                });
            }
            if (tagsDiff.remove && tagsDiff.remove.length) {
                actions.push({
                    action: 'deleteTags',
                    payload: {
                        device,
                        tagIds: tagsDiff.remove
                    }
                });
            }
        }
        return bbPromise
            .each(actions, item => {
                return dispatch(item.action, item.payload);
            })
            .then(() => {
                return device;
            });
    },
    addTags({ state }, { device, tagIds }) {
        // will add tags to device
        console.log('calling addTags');
        if (device && tagIds && tagIds.length) {
            return http
                .post(`${state.baseUrl}${device.id}/tags/`, tagIds, {
                    notifyRequestPending: false,
                    notifySuccess: false,
                    notifyError: false
                })
                .then(res => res.data);
        }
        return device;
    },
    deleteTags({ state }, { device, tagIds }) {
        // will delete tags from a device
        console.log('calling deleteTags');
        return http
            .delete(`${state.baseUrl}${device.id}/tags/`, tagIds, {
                notifyRequestPending: false,
                notifySuccess: false,
                notifyError: false
            })
            .then(res => res.data);
    },
    resetDeviceSettings({ state }, { deviceId }) {
        console.log('calling resetDeviceSettings', deviceId);
        return http.put(`${state.baseUrl}${deviceId}/reset`, null, {
            notifyRequestPending: true,
            notifyError: true,
            notifySuccess: true,
            successMessage: 'Device was reset successfully.'
        });
    },
    rebootDevice({ state }, { deviceId }) {
        console.log('calling rebootDevice', deviceId);
        return http.put(`${state.baseUrl}${deviceId}/rebootDevice`, null, {
            notifyRequestPending: true,
            notifyError: true,
            notifySuccess: true,
            successMessage: 'Device rebooted successfully.'
        });
    },
    updateClosedCaption({ state, dispatch }, { deviceId, closedCaption }) {
        console.log('calling updateClosedCaption', deviceId);
        return http
            .put(
                `${state.baseUrl}${deviceId}/setClosedCaptioning?flag=${
                    closedCaption ? 'on' : 'off'
                }`,
                null,
                {
                    notifyRequestPending: false,
                    notifyError: true,
                    notifySuccess: true,
                    successMessage: 'Closed caption updated successfully.'
                }
            )
            .then(() => {
                return dispatch('fetchPreferences', deviceId);
            });
    },
    updateTalkingGuide({ state, dispatch }, { deviceId, talkingGuide }) {
        console.log('calling updateTalkingGuide', deviceId);
        return http
            .put(
                `${state.baseUrl}${deviceId}/setTalkingGuide?flag=${
                    talkingGuide ? 'on' : 'off'
                }`,
                null,
                {
                    notifyRequestPending: false,
                    notifyError: true,
                    notifySuccess: true,
                    successMessage: 'Talking guide updated successfully.'
                }
            )
            .then(() => {
                return dispatch('fetchPreferences', deviceId);
            });
    },
    updateDefaultChannel({ state }, { deviceId, defaultChannel }) {
        console.log('calling updateDefaultChannel');
        if (deviceId && defaultChannel) {
            return http.patch(
                `${state.baseUrl}${deviceId}/defaultChannel`,
                { defaultChannel: defaultChannel },
                {
                    notifyRequestPending: false,
                    notifySuccess: false,
                    notifyError: false
                }
            );
        }
    },
    checkIsDeviceUnique({ state }, { id, hostMac }) {
        // will check if the device is unique or not
        // if isNewRecord there must be no device with that same name
        // if not isNewRecord there must be no more than 1 record with the same name
        console.log('calling checkIsDeviceUnique');
        let query = 'size=2';
        if (hostMac) {
            query += `&hostMac=${hostMac}`;
        }
        return http
            .get(`${state.baseUrl}search?${query}`, {
                notifyRequestPending: true,
                notifySuccess: false
            })
            .then(res => {
                return (
                    res.data.content.filter(item => {
                        return item.id !== id;
                    }).length <= 0
                );
            });
    },
    resetByIds({ state }, { deviceIds }) {
        // this action will get a list of api calls that
        console.log('calling device resetByIds');
        return bbPromise.each(deviceIds, id => {
            return http.put(`${state.baseUrl}${id}/reset`, null, {
                notifyRequestPending: false,
                notifySuccess: false,
                notifyError: false
            });
        });
    },
    remoteTune({ state, dispatch }, { deviceIds, channelNumber }) {
        console.log('calling remoteTune');
        const successMessage = 'Remote tune have been sent successfully';
        const failMessage =
            'Remote tune failed due to some device communication issue!';
        return bbPromise
            .each(deviceIds, id => {
                return http.put(
                    `${state.baseUrl}${id}/remoteTune/?channelNumber=${channelNumber}`,
                    null,
                    {
                        notifyRequestPending: true,
                        notifySuccess: false,
                        notifyError: false
                    }
                );
            })
            .then(() => {
                notifySuccess(dispatch, successMessage);
            })
            .catch(error => {
                notifyError(dispatch, failMessage, error);
            })
            .finally(() => {
                dispatch('notifier/reset', null, { root: true });
            });
    }
};

export default {
    namespaced: true,
    state: defaultState,
    mutations,
    actions
};
